import supervised from "../../../assets/img/blog/mathematicalArtificialIntelligence/figures/supervised.png"
import crossval from "../../../assets/img/blog/mathematicalArtificialIntelligence/figures/crossval.png"
import voronoi from "../../../assets/img/blog/mathematicalArtificialIntelligence/figures/voronoi.png"
import {getRandomColor} from "../../../utils/GetRandomColor";

export default function MachineLearning() {
    const imgStyle = {
        backgroundColor: "#E9E8E6",
        border: `5px solid ${getRandomColor()}`,
    }
    
    const altTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
        textAlign: "center",
        marginTop: "-10px",
        fontStyle: "italic",
    }
    
    return (
        <div className={"blog-body"}>
            <h2>Machine Learning</h2>
            <p>
                By being only application of artificial intelligence so far, work principle of algorithms that
                enhance themselves by using data and training are called machine learning. Different
                machine learning models work better with suited purposes, not every model is capable of
                achieving any objective. There are four types of machine learning algorithms: supervised
                learning, semi-supervised learning, unsupervised learning and reinforcement learning.
            </p>

            <h3>Supervised Learning</h3>
            <p>
                Models that work with supervised learning principle learns with features and prediction
                data. Initially, data split into two uneven parts: training data and validation data. This
                procedure prevents overfitting. Then training data is used for training the model, model
                learns relations between features and prediction data. Next, predictions that made by
                model using features from validation data are compared to prediction from validation
                data to measure errors. Refer Figure 1 for visualization.
            </p>

            <img style={imgStyle} className={"blog-img"} src={supervised} alt="supervised learning"/>
            <div style={altTextStyle}><p><b>Figure 1:</b> Supervised learning model working principle.</p></div>

            <h3>Unsupervised Learning</h3>
            <p>
                Unsupervised learning is a learning method used for purposes like grouping or dimensionality
                reduction without using initial example. Unlike supervised learning, machine detects
                patterns all by itself, using these patterns to group objects into clusters. K-means clustering
                is
                one the most widely used unsupervised algorithms. It clusters objects around k
                centroids by measuring distance between centroid and data, and rearranging the locations
                of points, creating Voronoi tessellations. Essentially, k-means learns boundaries between
                these tessellations, can be seen in Figure 2.
            </p>
            <img style={imgStyle} className={"blog-img"} src={voronoi} alt="voronoi tessellations"/>
            <div style={altTextStyle}><p><b>Figure 2:</b> Data clustered with the k-means algorithm, k=3.</p></div>

            <h3>Semi-Supervised Learning</h3>
            <p>
                Semi-supervised learning models are a blend of supervised and unsupervised learning
                models. Used for labelling unlabeled data with the small example of labeled data. Natural
                language processing is an example for this learning type.
            </p>

            <h3>Reinforcement Learning</h3>
            <p>
                Reinforcement learning is a machine learning technique that uses no data, rather rewards
                the model to continue on desired path or gives feedback to readjust itself depending on the
                outcome. Creating a consistent simulation is a key factor, reinforcement learning works
                wonders with simulations with strictly limited rules like chess, go and shogi. Google DeepMind's
                chess bot AlphaZero built on reinforcement learning algorithms, exceeds its
                most advanced rival Stockfish within just 4 hours of training (AlphaZero: Shedding new
                light on the grand games of chess, shogi and Go, n.d.).
            </p>

            <h2>Feature Engineering</h2>
            <p>
                Feature engineering is an engineering branch works on acquiring the best outcome from
                a machine learning procedure by discovering new features from the raw data.
                Along
                technical requirements, feature engineers also need common sense to make connection
                between features so researching the problem domain could be helpful.
            </p><p>
            Data visualization is a great tool for future engineering, it can help feature engineers
            to detect anomalies, outliers, complicated relations.
        </p>

            <h3>Choosing the Right Model</h3>
            <p>
                Unlike popular opinion, machine learning models are not magical instruments that capable
                of achieving anything, they are tools designed for specialized purposes. Each model has
                its advantages and disadvantages, thus selecting the right model is essential.
            </p>

            <h3>Creating Features</h3>
            <p>Features are created by manipulating the raw data. Each model is different, meaning
                different kind of approaches are necessary. For example linear models often shows improved
                performance with normalized features, normalization especially needed for neural
                networks but sometimes tree models can also gain advantage from it. Created features can
                be sums, differences, ratios and so on. Even though model can learn it itself, it can still
                improve with the particularly implemented feature if it is exclusively important. More
                complex the features are, more difficult for model to learn.</p>
            <p> Categorical features can either be dropped or encoded.
                There are three types of
                encoding: ordinal encoding, one-hot encoding or target encoding.
            </p>

            <ul>
                <li><b>Ordinal encoding:</b> Assigning values to each categorical data. Used for ordinal
                    variables.
                </li>
                <li><b>One-hot encoding:</b> Creating new columns for each categorical data and filling
                    these columns with Boolean values, expressing if these variables are a part of the
                    data or not. Used for nominal variables.
                </li>
                <li><b>Target encoding:</b> Replacing categorical value with some numerical value acquired
                    from the target. Variables low in count may reflect their properties incorrectly, so
                    acquired numerical property for these variables needs smoothing.
                </li>
            </ul>

            <h3>Overfitting and Underfitting</h3>
            <p>
                Underfitting is a type of failure when models cannot capture the patterns and put on bad
                performance in the training data. This problem generally occurs when the volume of data
                remains insufficient, or when the chosen model is not suitable for the data.</p><p>
                Overfitting is type of failure when model performs extremely well in the training
                data, but not as well in the validation or test data. This problem occurs when model
                capture patterns specific to the given data, too specific that cannot be found in future
                predictions. Engineers split data into train data and validation data, train model with
                the train data and measure its performance with the validation data to prevent overfitting
                from happening. See Figure 1.</p><p>
            Overfitting should be taken into consideration while creating features, joining created feature
            to
            validation set instead of creating it from validation data as well secures
            independence and prevents overfitting.
        </p>

            <h3>Cross Validation</h3>
            <p>
                While working with considerably small data sets, performance of the model can be boosted
                with validating the model by different validation splits. This technique is called
                cross-validation.
                If the volume of the data is big enough, cross-validation is not necessarily
                needed, train data may be able to capture patterns without needing to change it.
                Cross-validation
                process is visually represented in Figure 3.
            </p>
            
            <img style={imgStyle} className={"blog-img"} src={crossval} alt="cross-validation"/>
            <div style={altTextStyle}><p><b>Figure 3:</b> Cross-validation process.</p></div>

            <h3>Data Leakage</h3>
            <p>
                A data leakage is a kind of failure happens when undesired data is used to train model
                along with the training data. Models with data leakage performs extraordinarily well during test
                process but fails in real life applications. Data leakage happens in two conditions:
            </p>

            <ul>
                <li>
                    <b>Target Leakage:</b> When the data contains information that would not be available when the
                    prediction is occurring.
                </li>
                <li><b>Train-test contamination:</b> When train data and validation data shares information.</li>
            </ul>
        </div>
    );
}